import React from "react";
import DrawerCustom from "../../../../shared/general/Drawer";
import { useDispatch, useSelector } from "react-redux";
import { Grid, MenuItem, Typography } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import TextFieldTheme from "../../../../shared/general/TextFieldTheme";
import ButtonBlue from "../../../../shared/general/ButtonBlue";
import { getEmployeeProfile } from "../../../../../../actions/employee";
import { useTranslation } from "react-i18next";
import { updateProbationAdmin } from "../../../../../../actions/probation";
import { openNotificationAlert } from "../../../../../../actions/notificationAlert";
import dayjs from "dayjs";

const DialogEdit = ({ open, handleClose, probation }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { result: employeeProfile } = useSelector(
    (state) => state.employeeProfile
  );

  const validateYupSchema = yup.object({
    probationResult: yup.number().nullable(),
    adminExpandTime: yup
      .number()
      .integer()
      .transform((value, originalValue) => originalValue === '' ? null : value)
      .max(180)
      .min(-180)
      .nullable()
      .required(),
  });

  const {
    control,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      probationResult:
        probation.adminResult !== null
          ? probation.adminResult
          : probation.managerLV2Result !== null
          ? probation.managerLV2Result
          : probation.managerLV1Result,
      adminExpandTime: probation.expandTime ? probation.expandTime : "",
    },
    resolver: yupResolver(validateYupSchema),
  });

  const onSubmit = async (data) => {
    const formData = {
      idProbation: probation.idProbation,
      probationResult: data.probationResult,
      adminExpandTime: data.adminExpandTime ? data.adminExpandTime : 0,
      probationDate: dayjs(probation.defaultProbationDate).add(data.adminExpandTime, "day").format("YYYY-MM-DD"),
      oldData: {
        idEmp: employeeProfile.idEmp,
        menuName: "HR CORE/ข้อมูลพนักงาน-ทดลองงาน",
        typeName: "Edit Probation Result/แก้ไขผลการทดลองงาน",
        action: 1,
        key: { idProbation: probation.idProbation },
        probationResult:
          probation.adminResult !== null
            ? probation.adminResult
            : probation.managerLV2Result !== null
            ? probation.managerLV2Result
            : probation.managerLV1Result,
      }
    };

    const res = await dispatch(updateProbationAdmin(formData));
    if (res && res.status === 200) {
      dispatch(openNotificationAlert({ message: "success", type: "success" }));
      dispatch(getEmployeeProfile(employeeProfile.idEmp));
    } else {
      dispatch(openNotificationAlert({ message: "error", type: "error" }));
    }
    handleClose();
  };

  return (
    <DrawerCustom
      title={`${t("EditProbationResult")}`}
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "500px",
        },
      }}
    >
      {probation && (
        <Grid container spacing={2} padding={2}>
          <Grid item xs={12}>
            <Typography fontSize="16px" fontWeight="600">
              {`${t("ProbationRound")} ${probation.probationRound}`}
            </Typography>
          </Grid>
          {Boolean(probation.expandTime) && (
            <Grid item xs={12} display={"flex"} alignItems={"center"} gap={1}>
              <AddCircleIcon />
              <Typography>
                {`${t("ExtendPeriod")}: ${probation.expandTime} ${t(
                  "Days"
                )}`}
              </Typography>
            </Grid>
          )}
          {probation.adminResult !== null && (
            <Grid item xs={12} display={"flex"} alignItems={"center"} gap={1}>
              <CheckCircleIcon />
              <Typography>
                {`${t("EvaluatedBy")} : ${probation.adminResult !== null
                    ? t("Administrator")
                    : i18n.resolvedLanguage === "th"
                      ? probation.approvedBy
                      : probation.approvedBy_EN
                  }`}
              </Typography>
            </Grid>
          )}
          {probation.result === null && (
            <Grid item xs={12}>
              <Typography marginBottom={1}>
                {t("ReasonForExtendingProbation")}
              </Typography>
              <TextFieldTheme
                inputProps={{ readOnly: true }}
                multiline
                value={probation.reasonExpandTime}
              />
            </Grid>
          )}
          {probation.result === 0 && (
            <Grid item xs={12}>
              <Typography marginBottom={1}>
                {t("ReasonForNotPassingProbation")}
              </Typography>
              <TextFieldTheme
                inputProps={{ readOnly: true }}
                multiline
                value={probation.reasonTermination}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div style={{ marginBottom: "1rem" }}>
                <Typography fontSize="14px" fontWeight="600" marginBottom={1}>
                  {t("ProbationResult")}
                </Typography>
                <Controller
                  name="probationResult"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextFieldTheme
                      {...field}
                      select
                      helperText={
                        fieldState.error && fieldState.error.message
                      }
                      error={Boolean(fieldState.error)}
                    >
                      <MenuItem value={1}>{t("PassedProbation")}</MenuItem>
                      <MenuItem value={0}>{t("NotPassedProbation")}</MenuItem>
                    </TextFieldTheme>
                  )}
                />
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <Typography fontSize="14px" fontWeight="600" marginBottom={1}>
                  {`${t("ExtendPeriod")} (${t("Days")})`}
                </Typography>
                <Controller
                  name="adminExpandTime"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextFieldTheme
                      {...field}
                      type="number"
                      helperText={fieldState.error && fieldState.error.message}
                      error={Boolean(fieldState.error)}
                    />
                  )}
                />
              </div>
              <div style={{ marginBottom: "1rem" }}>
                <Typography fontSize="14px" fontWeight="600" marginBottom={1}>
                  {t("ProbationaryExpirationDate")}
                </Typography>
                <Typography>
                  {dayjs(probation.defaultProbationDate)
                    .add(watch("adminExpandTime"), "day")
                    .format(
                      i18n.resolvedLanguage === "th"
                        ? "DD MMMM BBBB"
                        : "DD MMMM YYYY"
                    )}
                </Typography>
              </div>
              <div
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <ButtonBlue
                  variant="text"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {t("Cancel")}
                </ButtonBlue>
                <ButtonBlue
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {t("Save")}
                </ButtonBlue>
              </div>
            </form>
          </Grid>
        </Grid>
      )}
    </DrawerCustom>
  );
};

export default DialogEdit;
